import { create } from 'zustand';
import { apiService } from '@/services/api';
import type { Match } from '@/types';

function parseToYMD(dateStr: string | number): string | null {
  if (dateStr == null) return null;
  if (typeof dateStr === 'number' && !isNaN(dateStr)) {
    // Some APIs return seconds instead of milliseconds.
    const normalized = dateStr < 10_000_000_000 ? dateStr * 1000 : dateStr;
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  if (typeof dateStr !== 'string') return null;
  const s = String(dateStr).trim();
  if (!s) return null;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  const dmyWithTime = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?)?$/);
  if (dmyWithTime) return `${dmyWithTime[3]}-${dmyWithTime[2].padStart(2, '0')}-${dmyWithTime[1].padStart(2, '0')}`;
  const dmyDash = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmyDash) return `${dmyDash[3]}-${dmyDash[2].padStart(2, '0')}-${dmyDash[1].padStart(2, '0')}`;
  const dmyDashWithTime = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?)?$/);
  if (dmyDashWithTime) return `${dmyDashWithTime[3]}-${dmyDashWithTime[2].padStart(2, '0')}-${dmyDashWithTime[1].padStart(2, '0')}`;
  const dmyDot = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dmyDot) return `${dmyDot[3]}-${dmyDot[2].padStart(2, '0')}-${dmyDot[1].padStart(2, '0')}`;
  const dmyDotWithTime = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?)?$/);
  if (dmyDotWithTime) return `${dmyDotWithTime[3]}-${dmyDotWithTime[2].padStart(2, '0')}-${dmyDotWithTime[1].padStart(2, '0')}`;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Heure affichée / API : "14:30", "14:30:00", "14h30", "14 h 30", etc. */
function parseHeureToHms(heure: string | undefined): { h: number; m: number; s: number } {
  const raw = (heure ?? '').trim().toLowerCase().replace(/\u00a0/g, ' ');
  if (!raw) return { h: 0, m: 0, s: 0 };
  const colon = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (colon) {
    return {
      h: Math.min(23, Math.max(0, Number(colon[1]))),
      m: Math.min(59, Math.max(0, Number(colon[2]))),
      s: colon[3] != null ? Math.min(59, Math.max(0, Number(colon[3]))) : 0,
    };
  }
  const hFr = raw.match(/^(\d{1,2})\s*h\s*(\d{2})?\s*(?:min)?$/i);
  if (hFr) {
    const h = Math.min(23, Math.max(0, Number(hFr[1])));
    const m = hFr[2] != null ? Math.min(59, Math.max(0, Number(hFr[2]))) : 0;
    return { h, m, s: 0 };
  }
  return { h: 0, m: 0, s: 0 };
}

/**
 * Heure connue (évite l’affichage « --:-- » comme dans MatchCard).
 * Avec score final, l’heure n’est pas affichée : le match reste visible.
 */
export function matchHasKnownKickoffTime(m: Match): boolean {
  if (m.home_score != null && m.away_score != null) return true;
  const raw = (m.heure ?? '').trim().replace(/\u00a0/g, ' ');
  if (!raw) return false;
  if (raw.includes('--')) return false;
  const lower = raw.toLowerCase();
  return (
    /^\d{1,2}:\d{2}/.test(lower) ||
    /^\d{1,2}\s*h\s*\d{2}/.test(lower) ||
    /^\d{1,2}\s*h\s*$/.test(lower)
  );
}

/** Date/heure locale du coup d’envoi ; `null` si la date est absente ou invalide. */
export function getMatchKickoffDate(match: Pick<Match, 'date' | 'heure'>): Date | null {
  const ymd = parseToYMD(match.date);
  if (!ymd) return null;
  const parts = ymd.split('-').map(Number);
  const y = parts[0];
  const mo = parts[1];
  const d = parts[2];
  if (!y || !mo || !d) return null;
  const { h, m: min, s } = parseHeureToHms(match.heure);
  const dt = new Date(y, mo - 1, d, h, min, s);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Match commencé (score final ou coup d’envoi atteint). */
export function isMatchStarted(match: Match, at: Date = new Date()): boolean {
  if (match.home_score != null && match.away_score != null) return true;
  if (!matchHasKnownKickoffTime(match)) return false;
  const kickoff = getMatchKickoffDate(match);
  if (!kickoff) return false;
  return at.getTime() >= kickoff.getTime();
}

/** Timestamp local pour tri date + heure croissants ; non parsable → fin de liste. */
function matchDateTimeSortMs(m: Match): number {
  const dt = getMatchKickoffDate(m);
  return dt ? dt.getTime() : Number.MAX_SAFE_INTEGER;
}

export function getWeekendBounds(weekOffset: number): { start: Date; end: Date } {
  const now = new Date();
  const day = now.getDay();
  const daysToSaturday = day === 0 ? -1 : day === 6 ? 0 : 6 - day;
  const thisSaturday = new Date(now);
  thisSaturday.setDate(now.getDate() + daysToSaturday);
  thisSaturday.setHours(0, 0, 0, 0);
  const targetSaturday = new Date(thisSaturday);
  targetSaturday.setDate(thisSaturday.getDate() + weekOffset * 7);
  const targetSunday = new Date(targetSaturday);
  targetSunday.setDate(targetSaturday.getDate() + 1);
  targetSunday.setHours(23, 59, 59, 999);
  return { start: targetSaturday, end: targetSunday };
}

function isU17NationalComp(comp: string): boolean {
  return comp.includes('u17n') || comp.includes('u17 nat') || comp.includes('u17 national');
}

function isU19NationalComp(comp: string): boolean {
  return comp.includes('u19n') || comp.includes('u19 nat') || comp.includes('u19 national');
}

/** U15F / U18F ; U19 féminin dont U19NF (libellés FFF variables). */
function isUFemaleComp(comp: string, age: '15' | '18' | '19'): boolean {
  const u = `u${age}`;
  if (age === '19') {
    if (comp.includes('u19nf') || comp.includes('u19 nf') || comp.includes('u19n f')) return true;
  }
  if (comp.includes(`${u}f`) || comp.includes(`${u} f`) || comp.includes(`${u} féminin`) || comp.includes(`${u} feminin`)) return true;
  if (comp.includes(u) && (comp.includes('féminin') || comp.includes('feminin'))) return true;
  return false;
}

function isNationalDivisionComp(comp: string, level: 1 | 2 | 3): boolean {
  const n = String(level);
  return (
    comp.includes(`n${n}`) ||
    comp.includes(`national ${n}`) ||
    comp.includes(`national${n}`)
  );
}

/** Filtre compétition : U15/U18/U19 sans féminin ; variantes U17N, U19N, U15F, U18F, U19NF ; N1/N2/N3. */
function competitionMatchesFilter(competition: string, filterKey: string): boolean {
  const comp = competition.toLowerCase();
  const f = filterKey.toLowerCase();
  if (f === 'national') {
    return (
      competitionMatchesFilter(competition, 'u17n') ||
      competitionMatchesFilter(competition, 'u19n') ||
      competitionMatchesFilter(competition, 'u19nf') ||
      competitionMatchesFilter(competition, 'n1') ||
      competitionMatchesFilter(competition, 'n2') ||
      competitionMatchesFilter(competition, 'n3')
    );
  }
  if (f === 'u17n') return isU17NationalComp(comp);
  if (f === 'u19n') return isU19NationalComp(comp);
  if (f === 'u15f') return isUFemaleComp(comp, '15');
  if (f === 'u18f') return isUFemaleComp(comp, '18');
  if (f === 'u19nf') return isUFemaleComp(comp, '19');
  if (f === 'u17') return comp.includes('u17');
  if (f === 'u19') return comp.includes('u19') && !isUFemaleComp(comp, '19');
  if (f === 'u15') return comp.includes('u15') && !isUFemaleComp(comp, '15');
  if (f === 'u18') return comp.includes('u18') && !isUFemaleComp(comp, '18');
  if (f === 'n1') return isNationalDivisionComp(comp, 1);
  if (f === 'n2') return isNationalDivisionComp(comp, 2);
  if (f === 'n3') return isNationalDivisionComp(comp, 3);
  return comp.includes(f);
}

export function formatWeekendLabel(weekOffset: number): string {
  if (weekOffset === 0) return 'Ce week-end';
  const { start, end } = getWeekendBounds(weekOffset);
  const shortMonth = (d: Date) => d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '');
  const sameMonth = start.getMonth() === end.getMonth();
  if (sameMonth) return `${start.getDate()} - ${end.getDate()} ${shortMonth(start)}`;
  return `${start.getDate()} ${shortMonth(start)} - ${end.getDate()} ${shortMonth(end)}`;
}

interface MatchState {
  matches: Match[];
  isLoading: boolean;
  error: string | null;
  fromCache: boolean;
  currentFilter: string;
  searchQuery: string;
  weekOffset: number;
  fetchMatches: (forceRefresh?: boolean) => Promise<void>;
  setFilter: (filter: string) => void;
  setSearchQuery: (query: string) => void;
  setWeekOffset: (delta: number) => void;
  resetToCurrentWeek: () => void;
  getFilteredMatches: () => Match[];
}

export const useMatchStore = create<MatchState>((set, get) => ({
  matches: [],
  isLoading: false,
  error: null,
  fromCache: false,
  currentFilter: 'all',
  searchQuery: '',
  weekOffset: 0,

  fetchMatches: async (forceRefresh = false) => {
    set({ isLoading: true, error: null });
    try {
      const result = await apiService.getMatches(forceRefresh);
      set({ matches: result.data, fromCache: result.fromCache, isLoading: false });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Erreur de chargement', isLoading: false });
    }
  },

  setFilter: (filter: string) => set({ currentFilter: filter }),
  setSearchQuery: (query: string) => set({ searchQuery: query }),
  setWeekOffset: (delta: number) => set((state) => ({ weekOffset: state.weekOffset + delta })),
  resetToCurrentWeek: () => set({ weekOffset: 0 }),

  getFilteredMatches: () => {
    const { matches, currentFilter, searchQuery, weekOffset } = get();
    let filtered = matches;

    const { start, end } = getWeekendBounds(weekOffset);
    const toYMD = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const satYMD = toYMD(start);
    const sunYMD = toYMD(end);
    const weekendFiltered = filtered.filter((m) => {
      const raw = m as unknown as Record<string, unknown>;
      const dateStr = (m.date ?? raw.date_match ?? raw.dateMatch ?? raw.date_match_football ?? '') as string | number;
      const mYMD = parseToYMD(dateStr);
      if (!mYMD) return false;
      return mYMD === satYMD || mYMD === sunYMD;
    });
    filtered = weekendFiltered.filter((m) => matchHasKnownKickoffTime(m));

    if (currentFilter !== 'all') {
      filtered = filtered.filter((match) =>
        competitionMatchesFilter(match.competition || '', currentFilter)
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.homeTeam?.toLowerCase().includes(q) ||
          m.awayTeam?.toLowerCase().includes(q) ||
          m.stade?.toLowerCase().includes(q) ||
          m.ville?.toLowerCase().includes(q) ||
          m.nom?.toLowerCase().includes(q) ||
          m.competition?.toLowerCase().includes(q) ||
          m.cp?.includes(q)
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      const ta = matchDateTimeSortMs(a);
      const tb = matchDateTimeSortMs(b);
      if (ta !== tb) return ta - tb;
      return String(a.idmatch_football).localeCompare(String(b.idmatch_football));
    });
    return sorted;
  },
}));
